import * as Sentry from '@sentry/vue';

// Recuperación de cargas bootstrap (patrón "recuperación, no supervivencia",
// portado del form de destinatario / ARCADI-EB): auto-retry acotado con
// backoff, identidad por generación (sin cadenas zombi), refresh al volver la
// conexión, y estado honesto para que la UI muestre "Reintentar" en vez de
// quedarse muda (ARCADI-E9: el simulador de la landing quedaba a medio armar
// ante cualquier blip de red móvil, sin mensaje ni salida).
//
// Uso en un componente (Options API):
//   this.loadWithRecovery('countries', {
//     request: () => SimulatorApi.getCountries(...),
//     assign: ({ data }) => { ... },            // corre solo con respuesta sana
//     validate: (data) => Array.isArray(data),  // opcional; default: siempre ok
//     canRun: () => Boolean(...),               // opcional; re-chequeado ANTES
//                                               // de cada intento (los guards
//                                               // del caller también valen
//                                               // para los retries)
//   });
// Template: anyBootstrapError() → franja con retryFailedBootstraps().
// Solo cuentan como error de franja los keys que NUNCA cargaron (everOk
// false): un refresh fallido sobre datos ya poblados no tapa un form
// funcional con una franja de error dura.
export default {
  data() {
    return {
      bootstrapLoads: {},
    };
  },
  created() {
    // Config y timers fuera de la reactividad (funciones y handles, no estado).
    this.$_bootstrapConfigs = {};
    this.$_bootstrapTimers = {};
    this.$_bootstrapSeqs = {};
    this.$_bootstrapOnlineHandler = () => this.retryFailedBootstraps();
    window.addEventListener('online', this.$_bootstrapOnlineHandler);
  },
  beforeUnmount() {
    window.removeEventListener('online', this.$_bootstrapOnlineHandler);
    Object.values(this.$_bootstrapTimers).forEach((timer) => clearTimeout(timer));
    // Invalida toda cadena en vuelo: un retry post-unmount no debe tocar nada.
    Object.keys(this.$_bootstrapSeqs).forEach((key) => {
      this.$_bootstrapSeqs[key] += 1;
    });
  },
  methods: {
    loadWithRecovery(key, config = null, attempt = 0, seq = null) {
      if (config) this.$_bootstrapConfigs[key] = config;
      const activeConfig = this.$_bootstrapConfigs[key];
      if (!activeConfig) return;

      // Generación monótona: una carga nueva invalida cualquier cadena de
      // retry anterior del mismo key (sin esto, un fallo tardío de la cadena
      // vieja pisaría datos ya cargados por la nueva).
      const chainSeq = seq === null
        ? (this.$_bootstrapSeqs[key] = (this.$_bootstrapSeqs[key] || 0) + 1)
        : seq;

      // El guard del caller vale también para los retries: si el estado del
      // componente cambió durante el backoff (p.ej. corredor sin destinos),
      // la cadena muere en 'idle' en vez de disparar una consulta que el
      // componente decidió suprimir (franja de error falsa + 422 circular).
      if (activeConfig.canRun && !activeConfig.canRun()) {
        this.$_setBootstrapState(key, 'idle');
        return;
      }
      this.$_setBootstrapState(key, 'loading');

      activeConfig.request().then((response) => {
        if (chainSeq !== this.$_bootstrapSeqs[key]) return; // cadena obsoleta
        if (activeConfig.validate && !activeConfig.validate(response.data)) {
          const invalid = new Error(`invalid bootstrap payload for ${key}`);
          invalid.isInvalidPayload = true; // transitorio por diseño (fantasma/vacío)
          throw invalid;
        }
        activeConfig.assign(response);
        this.bootstrapLoads[key] = { state: 'ok', everOk: true };
      }).catch((error) => {
        if (chainSeq !== this.$_bootstrapSeqs[key]) return;

        // Solo red y payloads inválidos son reintentables. Cualquier otra
        // excepción es un BUG (p.ej. dentro de assign): reportarlo — sin esto
        // el mixin cegaría el canal de Sentry que descubrió toda la familia
        // ARCADI-E9/EP/EN/F4 — y no reintentar sobre estado a medio asignar.
        const retryable = Boolean(
          error?.isAxiosError || error?.isPhantomResponse || error?.isInvalidPayload,
        );
        if (!retryable) {
          this.$_setBootstrapState(key, 'error');
          console.error(`[bootstrap-recovery] '${key}' crashed outside the network path`, error); // eslint-disable-line no-console
          if (Sentry.isInitialized && Sentry.isInitialized()) Sentry.captureException(error);
          return;
        }
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          // Offline confirmado: no quemar reintentos; el listener de 'online'
          // relanza la carga al volver la conexión.
          this.$_setBootstrapState(key, 'error');
          return;
        }
        if (attempt < 2) {
          const delayMs = attempt === 0 ? 1000 : 3000;
          this.$_bootstrapTimers[key] = setTimeout(() => {
            if (chainSeq === this.$_bootstrapSeqs[key]) {
              this.loadWithRecovery(key, null, attempt + 1, chainSeq);
            }
          }, delayMs);
          return; // sigue en 'loading' durante el backoff
        }
        this.$_setBootstrapState(key, 'error');
      });
    },
    $_setBootstrapState(key, state) {
      const everOk = (this.bootstrapLoads[key] || {}).everOk === true;
      this.bootstrapLoads[key] = { state, everOk };
    },
    bootstrapState(key) {
      return (this.bootstrapLoads[key] || {}).state || 'idle';
    },
    anyBootstrapError() {
      // Franja dura solo para bootstraps que NUNCA cargaron: un refresh
      // fallido con datos ya poblados degrada en silencio (los watchers y el
      // listener 'online' lo re-driven) en vez de tapar un form funcional.
      return Object.values(this.bootstrapLoads).some(
        (load) => load.state === 'error' && !load.everOk,
      );
    },
    anyBootstrapLoading() {
      return Object.values(this.bootstrapLoads).some((load) => load.state === 'loading');
    },
    retryFailedBootstraps() {
      Object.entries(this.bootstrapLoads).forEach(([key, load]) => {
        if (load.state === 'error') this.loadWithRecovery(key);
      });
    },
  },
};
